import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { useModuleStore } from '../stores/moduleStore';
import { usePortalStore } from '../stores/portalStore';
import { AppLayout } from '../shared/components/AppLayout';
import { api } from '../lib/api';

// ─── Eager loaded (no lazy — always needed) ───────────────────────────────────
import { LoginPage } from '../modules/auth/pages/LoginPage';
import { DashboardPage } from '../modules/dashboard/pages/DashboardPage';
import { StudentLoginPage } from '../modules/portal/student/StudentLoginPage';
import { ParentLoginPage } from '../modules/portal/parent/ParentLoginPage';
import { OnboardingWizard } from '../modules/onboarding/OnboardingWizard';

// ─── Module pages — lazy loaded ───────────────────────────────────────────────
const StudentsList           = lazy(() => import('../modules/academic/pages/StudentsList').then(m => ({ default: m.StudentsList })));
const TimetablePage          = lazy(() => import('../modules/academic/pages/TimetablePage').then(m => ({ default: m.TimetablePage })));
const EnrollStudent          = lazy(() => import('../modules/academic/pages/EnrollStudent').then(m => ({ default: m.EnrollStudent })));
const AttendancePage         = lazy(() => import('../modules/academic/pages/AttendancePage').then(m => ({ default: m.AttendancePage })));
const ClassesPage            = lazy(() => import('../modules/academic/pages/ClassesPage').then(m => ({ default: m.ClassesPage })));
const ExamSchedulePage       = lazy(() => import('../modules/exams/pages/ExamSchedulePage').then(m => ({ default: m.ExamSchedulePage })));
const GradeEntryPage         = lazy(() => import('../modules/exams/pages/GradeEntryPage').then(m => ({ default: m.GradeEntryPage })));
const ReportCardsPage        = lazy(() => import('../modules/exams/pages/ReportCardsPage').then(m => ({ default: m.ReportCardsPage })));
const BursarDashboard        = lazy(() => import('../modules/finance/pages/BursarDashboard').then(m => ({ default: m.BursarDashboard })));
const InvoicesList           = lazy(() => import('../modules/finance/pages/InvoicesList').then(m => ({ default: m.InvoicesList })));
const PaymentsPage           = lazy(() => import('../modules/finance/pages/PaymentsPage').then(m => ({ default: m.PaymentsPage })));
const FeeStructuresPage      = lazy(() => import('../modules/finance/pages/FeeStructuresPage').then(m => ({ default: m.FeeStructuresPage })));
const DefaultersPage         = lazy(() => import('../modules/finance/pages/DefaultersPage').then(m => ({ default: m.DefaultersPage })));
const StaffList              = lazy(() => import('../modules/hr/pages/StaffList').then(m => ({ default: m.StaffList })));
const PayrollRuns            = lazy(() => import('../modules/hr/pages/PayrollRuns').then(m => ({ default: m.PayrollRuns })));
const LeavePage              = lazy(() => import('../modules/hr/pages/LeavePage').then(m => ({ default: m.LeavePage })));
const EmisPage               = lazy(() => import('../modules/government/pages/EmisPage').then(m => ({ default: m.EmisPage })));
const UnebPage               = lazy(() => import('../modules/government/pages/UnebPage').then(m => ({ default: m.UnebPage })));
const UbtebPage              = lazy(() => import('../modules/government/pages/UbtebPage').then(m => ({ default: m.UbtebPage })));
const LibraryPage            = lazy(() => import('../modules/library/pages/LibraryPage').then(m => ({ default: m.LibraryPage })));
const HostelPage             = lazy(() => import('../modules/hostel/pages/HostelPage').then(m => ({ default: m.HostelPage })));
const TransportPage          = lazy(() => import('../modules/transport/pages/TransportPage').then(m => ({ default: m.TransportPage })));
const AssetsPage             = lazy(() => import('../modules/assets/pages/AssetsPage').then(m => ({ default: m.AssetsPage })));
const AnalyticsPage          = lazy(() => import('../modules/analytics/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const AiInsightsPage         = lazy(() => import('../modules/analytics/pages/AiInsightsPage').then(m => ({ default: m.AiInsightsPage })));
const BankReconciliationPage = lazy(() => import('../modules/finance/pages/BankReconciliationPage').then(m => ({ default: m.BankReconciliationPage })));
const ExpensesPage           = lazy(() => import('../modules/expenses/pages/ExpensesPage').then(m => ({ default: m.ExpensesPage })));
const VatPage                = lazy(() => import('../modules/vat/pages/VatPage').then(m => ({ default: m.VatPage })));
const SettingsPage           = lazy(() => import('../modules/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const UsersPage              = lazy(() => import('../modules/users/pages/UsersPage').then(m => ({ default: m.UsersPage })));

// ─── Portal pages — lazy loaded ───────────────────────────────────────────────
const StudentDashboardPage  = lazy(() => import('../modules/portal/student/StudentDashboardPage').then(m => ({ default: m.StudentDashboardPage })));
const StudentGradesPage     = lazy(() => import('../modules/portal/student/StudentGradesPage').then(m => ({ default: m.StudentGradesPage })));
const StudentAttendancePage = lazy(() => import('../modules/portal/student/StudentAttendancePage').then(m => ({ default: m.StudentAttendancePage })));
const StudentFeesPage       = lazy(() => import('../modules/portal/student/StudentFeesPage').then(m => ({ default: m.StudentFeesPage })));
const StudentReportsPage    = lazy(() => import('../modules/portal/student/StudentReportsPage').then(m => ({ default: m.StudentReportsPage })));
const ParentChildrenPage    = lazy(() => import('../modules/portal/parent/ParentChildrenPage').then(m => ({ default: m.ParentChildrenPage })));
const ParentChildDetailPage = lazy(() => import('../modules/portal/parent/ParentChildDetailPage').then(m => ({ default: m.ParentChildDetailPage })));

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 192 }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%',
        border: '2px solid var(--border)',
        borderTopColor: 'var(--accent)',
        animation: 'spin 0.6s linear infinite',
      }} />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// ─── Guards ───────────────────────────────────────────────────────────────────

function RequireAuth() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const accessToken = useAuthStore(s => s.accessToken);
  const setAccessToken = useAuthStore(s => s.setAccessToken);
  const clearAuth = useAuthStore(s => s.clearAuth);
  const location = useLocation();
  const refreshModules = useModuleStore(s => s.refresh);

  // On page reload: isAuthenticated is restored from localStorage but accessToken
  // is not — silently use the httpOnly refresh cookie to get a new one.
  const { data: tokenReady, isLoading: tokenLoading } = useQuery({
    queryKey: ['silent-refresh'],
    queryFn: async () => {
      if (accessToken) return true;
      try {
        const res = await api.post('/auth/refresh');
        setAccessToken(res.data.data.access_token);
        return true;
      } catch {
        clearAuth();
        return false;
      }
    },
    enabled: isAuthenticated,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (isAuthenticated && (accessToken || tokenReady)) refreshModules().catch(() => {});
  }, [isAuthenticated, tokenReady]);

  const { data: onboardingData } = useQuery({
    queryKey: ['onboarding-state'],
    queryFn: () => api.get<{ data: { completed: boolean } }>('/onboarding').then(r => r.data.data),
    enabled: isAuthenticated && (!!accessToken || tokenReady === true),
    staleTime: 60_000,
  });

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Show spinner while silently refreshing the token on reload
  if (tokenLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: '2px solid var(--border)',
          borderTopColor: 'var(--accent)',
          animation: 'spin 0.6s linear infinite',
        }} />
      </div>
    );
  }

  // Silent refresh failed — clearAuth already called, redirect to login
  if (tokenReady === false) return <Navigate to="/login" replace />;

  // Redirect to wizard if setup not completed (skip if already on /onboarding)
  if (onboardingData && !onboardingData.completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

function RequireModule({ slug }: { slug: string }) {
  const isEnabled = useModuleStore(s => s.isEnabled);
  if (!isEnabled(slug)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function RequireStudentPortal() {
  const isAuthenticated = usePortalStore(s => s.isAuthenticated);
  const role = usePortalStore(s => s.auth?.role);
  if (!isAuthenticated || role !== 'student') return <Navigate to="/portal/student/login" replace />;
  return <Outlet />;
}

function RequireParentPortal() {
  const isAuthenticated = usePortalStore(s => s.isAuthenticated);
  const role = usePortalStore(s => s.auth?.role);
  if (!isAuthenticated || role !== 'parent') return <Navigate to="/portal/parent/login" replace />;
  return <Outlet />;
}

// ─── Router ───────────────────────────────────────────────────────────────────

function RequireAuthBare() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },

  // ─── Onboarding wizard (auth required, no AppLayout) ──────────────────────
  {
    element: <RequireAuthBare />,
    children: [
      { path: '/onboarding', element: <OnboardingWizard /> },
    ],
  },

  // ─── Student portal (unauthenticated) ──────────────────────────────────────
  { path: '/portal/student/login', element: <StudentLoginPage /> },

  // ─── Student portal (authenticated) ───────────────────────────────────────
  {
    element: <RequireStudentPortal />,
    children: [
      { path: '/portal/student',            element: <Lazy><StudentDashboardPage /></Lazy> },
      { path: '/portal/student/grades',     element: <Lazy><StudentGradesPage /></Lazy> },
      { path: '/portal/student/attendance', element: <Lazy><StudentAttendancePage /></Lazy> },
      { path: '/portal/student/fees',       element: <Lazy><StudentFeesPage /></Lazy> },
      { path: '/portal/student/reports',    element: <Lazy><StudentReportsPage /></Lazy> },
    ],
  },

  // ─── Parent portal (unauthenticated) ──────────────────────────────────────
  { path: '/portal/parent/login', element: <ParentLoginPage /> },

  // ─── Parent portal (authenticated) ────────────────────────────────────────
  {
    element: <RequireParentPortal />,
    children: [
      { path: '/portal/parent',                        element: <Lazy><ParentChildrenPage /></Lazy> },
      { path: '/portal/parent/child/:studentId',       element: <Lazy><ParentChildDetailPage /></Lazy> },
    ],
  },

  {
    element: <RequireAuth />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },

      // Academic
      {
        element: <RequireModule slug="academic" />,
        children: [
          { path: 'students',              element: <Lazy><StudentsList /></Lazy> },
          { path: 'students/new',          element: <Lazy><EnrollStudent /></Lazy> },
          { path: 'academic/classes',      element: <Lazy><ClassesPage /></Lazy> },
          { path: 'academic/attendance',   element: <Lazy><AttendancePage /></Lazy> },
          { path: 'academic/timetable',    element: <Lazy><TimetablePage /></Lazy> },
        ],
      },

      // Exams
      {
        element: <RequireModule slug="academic" />,
        children: [
          { path: 'exams',               element: <Lazy><ExamSchedulePage /></Lazy> },
          { path: 'exams/grades',        element: <Lazy><GradeEntryPage /></Lazy> },
          { path: 'exams/report-cards',  element: <Lazy><ReportCardsPage /></Lazy> },
        ],
      },

      // Finance
      {
        element: <RequireModule slug="finance" />,
        children: [
          { path: 'finance',                   element: <Lazy><BursarDashboard /></Lazy> },
          { path: 'finance/invoices',          element: <Lazy><InvoicesList /></Lazy> },
          { path: 'finance/payments',          element: <Lazy><PaymentsPage /></Lazy> },
          { path: 'finance/fee-structures',    element: <Lazy><FeeStructuresPage /></Lazy> },
          { path: 'finance/defaulters',        element: <Lazy><DefaultersPage /></Lazy> },
          { path: 'finance/reconciliation',    element: <Lazy><BankReconciliationPage /></Lazy> },
          { path: 'finance/expenses',          element: <Lazy><ExpensesPage /></Lazy> },
          { path: 'finance/vat',               element: <Lazy><VatPage /></Lazy> },
        ],
      },

      // HR
      {
        element: <RequireModule slug="hr" />,
        children: [
          { path: 'hr/staff',   element: <Lazy><StaffList /></Lazy> },
          { path: 'hr/leave',   element: <Lazy><LeavePage /></Lazy> },
          { path: 'hr/payroll', element: <Lazy><PayrollRuns /></Lazy> },
        ],
      },

      // Government
      {
        children: [
          { path: 'government/emis',  element: <Lazy><EmisPage /></Lazy> },
          { path: 'government/uneb',  element: <Lazy><UnebPage /></Lazy> },
          { path: 'government/ubteb', element: <Lazy><UbtebPage /></Lazy> },
        ],
      },

      // Library
      {
        element: <RequireModule slug="library" />,
        children: [
          { path: 'library', element: <Lazy><LibraryPage /></Lazy> },
        ],
      },

      // Hostel
      {
        element: <RequireModule slug="hostel" />,
        children: [
          { path: 'hostel', element: <Lazy><HostelPage /></Lazy> },
        ],
      },

      // Transport
      {
        element: <RequireModule slug="transport" />,
        children: [
          { path: 'transport', element: <Lazy><TransportPage /></Lazy> },
        ],
      },

      // Assets
      {
        element: <RequireModule slug="assets" />,
        children: [
          { path: 'assets', element: <Lazy><AssetsPage /></Lazy> },
        ],
      },

      // Analytics
      { path: 'analytics',             element: <Lazy><AnalyticsPage /></Lazy> },
      { path: 'analytics/ai',          element: <Lazy><AiInsightsPage /></Lazy> },

      // Users management
      { path: 'users', element: <Lazy><UsersPage /></Lazy> },

      // Settings
      { path: 'settings',              element: <Lazy><SettingsPage /></Lazy> },

      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
